import React, { useState } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Change_Variables_With_JavaScript() {
  const [primaryColor, setPrimaryColor] = useState("#3b82f6");
  const [secondaryColor, setSecondaryColor] = useState("#9333ea");
  const [bgColor, setBgColor] = useState("#fef3c7");

  const changeTheme = (theme) => {
    if (theme === "dark") {
      document.documentElement.style.setProperty("--primary-color", "#10b981");
      document.documentElement.style.setProperty(
        "--secondary-color",
        "#06b6d4"
      );
      document.documentElement.style.setProperty(
        "--background-color",
        "#111827"
      );
    } else if (theme === "light") {
      document.documentElement.style.setProperty("--primary-color", "#3b82f6");
      document.documentElement.style.setProperty(
        "--secondary-color",
        "#9333ea"
      );
      document.documentElement.style.setProperty(
        "--background-color",
        "#fef3c7"
      );
    }
  };

  const handleColorChange = (varName, value) => {
    document.documentElement.style.setProperty(varName, value);
  };

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">
        🎨 Advanced CSS: Change Variables with JavaScript
      </h1>

      <p className="tutorial-intro">
        CSS variables (custom properties) can be changed dynamically at runtime
        using JavaScript. This enables interactive themes, gradient changes,
        responsive adjustments, and component-specific overrides.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#ede9fe,#d8b4fe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#5b21b6" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use{" "}
            <code>
              document.documentElement.style.setProperty('--var-name', value)
            </code>{" "}
            to change a variable.
          </li>
          <li>
            Variables can control colors, spacing, shadows, gradients, and
            animations.
          </li>
          <li>
            Combine JS + CSS variables for dynamic themes and interactive UI.
          </li>
          <li>
            Component-level variables can also be changed dynamically for
            isolated effects.
          </li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">📝 Examples</h2>

        {/* 1. Theme Switcher */}
        <h3>1. Light/Dark Theme Switcher</h3>
        <CodeCopyBlock
          code={`document.documentElement.style.setProperty('--primary-color','#10b981');`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              background: "var(--primary-color,#3b82f6)",
              color: "white",
            }}
            onClick={() => changeTheme("light")}
          >
            Light Theme
          </button>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              background: "var(--secondary-color,#9333ea)",
              color: "white",
            }}
            onClick={() => changeTheme("dark")}
          >
            Dark Theme
          </button>
        </div>

        {/* 2. Dynamic Gradient Button */}
        <h3>2. Change Gradient Colors Dynamically</h3>
        <CodeCopyBlock
          code={`document.documentElement.style.setProperty('--gradient-start','#3b82f6');
document.documentElement.style.setProperty('--gradient-end','#9333ea');`}
        />
        <div
          style={{
            background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            display: "inline-block",
            marginBottom: "1rem",
          }}
        >
          Dynamic Gradient Button
        </div>
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <input
            type="color"
            value={primaryColor}
            onChange={(e) => {
              setPrimaryColor(e.target.value);
              handleColorChange("--primary-color", e.target.value);
            }}
          />
          <input
            type="color"
            value={secondaryColor}
            onChange={(e) => {
              setSecondaryColor(e.target.value);
              handleColorChange("--secondary-color", e.target.value);
            }}
          />
        </div>

        {/* 3. Dynamic Background Color */}
        <h3>3. Change Background Dynamically</h3>
        <div
          style={{
            background: bgColor,
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        >
          Background changes dynamically
        </div>
        <input
          type="color"
          value={bgColor}
          onChange={(e) => {
            setBgColor(e.target.value);
            handleColorChange("--background-color", e.target.value);
          }}
          style={{ marginBottom: "1rem" }}
        />

        {/* 4. Component-level Variable Override */}
        <h3>4. Component-level Overrides</h3>
        <div
          style={{
            "--card-color": primaryColor,
            background: "var(--card-color,#3b82f6)",
            padding: "20px",
            borderRadius: "12px",
            color: "white",
            marginBottom: "1rem",
          }}
        >
          Component Card with overridden variable
        </div>

        {/* 5. Interactive Slider Example */}
        <h3>5. Control Padding Dynamically</h3>
        <CodeCopyBlock
          code={`document.documentElement.style.setProperty('--btn-padding','20px 40px');`}
        />
        <button
          style={{
            padding: "var(--btn-padding,10px 20px)",
            borderRadius: "8px",
            background: "var(--primary-color,#3b82f6)",
            color: "white",
            border: "none",
            marginBottom: "1rem",
          }}
        >
          Dynamic Padding Button
        </button>
        <input
          type="range"
          min="10"
          max="50"
          defaultValue="20"
          onChange={(e) =>
            handleColorChange("--btn-padding", `${e.target.value}px 40px`)
          }
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#ede9fe,#d8b4fe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#5b21b6" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Create fully dynamic UI components controlled by CSS variables.
          </li>
          <li>
            Use sliders, color pickers, or input fields to change variables
            live.
          </li>
          <li>
            Combine with gradients, shadows, and transitions for interactive
            effects.
          </li>
          <li>Integrate with theme switchers, dashboards, or design tools.</li>
        </ul>
      </div>
    </div>
  );
}
